import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { CreateClientPlansDialog } from "./CreateClientPlansDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft,
  CheckCircle2,
  User,
  Building2,
  Calendar,
  Mail,
  FileSignature,
  Shield,
  Heart,
  Eye,
  Stethoscope,
  Clock,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import type { Quote, QuoteAcceptance } from "@shared/schema";
import { cn } from "@/lib/utils";

interface AcceptanceDetails {
  accepted: boolean;
  status: string | null; // "pending" | "accepted" | ...
  acceptance: QuoteAcceptance | null;
  quote: Quote;
  selectedPlans: Record<string, any>;
  premiumSummary: any;
}

interface SelectedPlanInfo {
  id: string;
  name: string;
  carrier: string;
  carrierName?: string;
  coverageType: string;
  metalTier?: string;
  premium: number;
  employerPays: number;
  employeePays: number;
  benefits?: Record<string, any>;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateInput: string | Date | null) => {
  if (!dateInput) return "N/A";
  const d = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getCoverageIcon = (type: string) => {
  switch (type?.toLowerCase()) {
    case "health":
    case "medical":
      return Heart;
    case "dental":
      return Stethoscope;
    case "vision":
      return Eye;
    default:
      return Shield;
  }
};

const getCoverageColor = (type: string) => {
  switch (type?.toLowerCase()) {
    case "health":
    case "medical":
      return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
    case "dental":
      return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300";
    case "vision":
      return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300";
    default:
      return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
  }
};

export function AcceptedQuoteDetails() {
  const [, params] = useRoute("/quoting/accepted/:id");
  const quoteId = params?.id ? parseInt(params.id) : null;
  const [createPlansOpen, setCreatePlansOpen] = useState(false);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<AcceptanceDetails>({
    queryKey: ["/api/quotes", quoteId, "acceptance"],
    queryFn: async () => {
      const response = await fetch(`/api/quotes/${quoteId}/acceptance`);
      if (!response.ok) throw new Error("Failed to fetch acceptance details");
      return response.json();
    },
    enabled: !!quoteId,
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/quotes/${quoteId}/confirm-acceptance`);
      if (!res.ok) throw new Error("Failed to confirm acceptance");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes", quoteId, "acceptance"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      toast({ title: "Acceptance confirmed", description: "The quote is now marked as accepted." });
    },
    onError: () => {
      toast({ title: "Couldn't confirm", description: "Please try again.", variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
          <CardContent className="py-8 text-center">
            <p className="text-red-700 dark:text-red-300">
              Failed to load acceptance details. Please try again.
            </p>
            <Link href="/quoting">
              <Button variant="outline" className="mt-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Quotes
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { accepted, status, acceptance, quote, selectedPlans, premiumSummary } = data;
  // Written by the create-plans endpoint, so the page shows the outcome rather
  // than offering to create the same plans again.
  const conversion = (quote as any)?.wizardState?._conversion as
    | { at: string; clientId: number; planIds: number[] }
    | undefined;

  // Only bail when there's truly no client response yet. A "pending" submission
  // renders below (with a Confirm button) same as an accepted one.
  if (!acceptance) {
    return (
      <div className="p-6">
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20">
          <CardContent className="py-8 text-center">
            <Clock className="h-12 w-12 mx-auto mb-4 text-amber-600" />
            <h3 className="text-lg font-semibold mb-2">No response yet</h3>
            <p className="text-muted-foreground mb-4">
              The client hasn't submitted their selections for this quote yet.
            </p>
            <Link href="/quoting">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Quotes
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPending = status === "pending";

  const selectedPlansJson = acceptance.selectedPlansJson as SelectedPlanInfo[] | null;
  // Null when the client accepted through the public link; set when a broker
  // recorded an agreement reached off the platform.
  const recordedByBroker = !!(acceptance as any).recordedByUserId;
  const selectedCarrier = selectedPlansJson?.[0]?.carrier || selectedPlansJson?.[0]?.carrierName || "Unknown";

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/quoting">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {isPending ? (
                <Clock className="h-6 w-6 text-amber-600" />
              ) : (
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              )}
              {isPending ? "Client Response — Pending" : "Accepted Quote"}
            </h1>
            <p className="text-muted-foreground">
              {quote.companyName} - Quote #{quote.id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isPending ? (
            <>
              <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-sm px-3 py-1">
                Awaiting confirmation
              </Badge>
              <Button
                onClick={() => confirmMutation.mutate()}
                disabled={confirmMutation.isPending}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {confirmMutation.isPending ? "Confirming..." : "Confirm & Accept"}
              </Button>
            </>
          ) : (
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-sm px-3 py-1">
              Accepted
            </Badge>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5" />
              Acceptance Details
            </CardTitle>
            <CardDescription>
              {recordedByBroker
                ? "Recorded from an agreement made outside the platform"
                : "Information about who accepted this quote"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* A broker-recorded acceptance has no signer and no contribution
                strategy, so the panel below is mostly "N/A" and the plan setup
                opens with empty contributions. Say why, or it reads as a bug. */}
            {recordedByBroker && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                <p className="flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    This was marked accepted by a broker, not signed by the client here. The
                    selections and the contribution split were agreed elsewhere, so there's no
                    signature on file and you'll enter the contributions when setting up the plans.
                    {acceptance.comments ? ` Note: ${acceptance.comments}` : ""}
                  </span>
                </p>
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Signer Name</p>
                <p className="font-medium">{acceptance.signerName || "N/A"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Building2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Title</p>
                <p className="font-medium">{acceptance.signerTitle || "N/A"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <Mail className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{acceptance.signerEmail || "N/A"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Calendar className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{isPending ? "Submitted On" : "Accepted On"}</p>
                <p className="font-medium">
                  {formatDate(isPending ? (acceptance as any).updatedAt || (acceptance as any).createdAt : acceptance.acceptedAt)}
                </p>
              </div>
            </div>
            {/* What the client set on the Contribution Calculator. Until now this
                only existed in their browser, so the broker had no way to see the
                split they agreed to, only the resulting dollar totals. */}
            {(acceptance as any).selectedStrategyJson && (() => {
              const strategy = (acceptance as any).selectedStrategyJson;
              const isFlat = strategy.mode === "flat";
              // Older rows used the pre-rename keys, which held the same numbers.
              const employeeShare =
                strategy.employerPercentEmployeeTier ?? strategy.employeeContributionPercent;
              const dependentShare =
                strategy.employerPercentDependents ?? strategy.dependentContributionPercent;
              const format = (percent: number, flat: number | undefined) =>
                isFlat && flat != null
                  ? `$${Number(flat).toLocaleString()}`
                  : percent != null
                    ? `${percent}%`
                    : "N/A";
              return (
                <div className="pt-2">
                  <p className="text-sm text-muted-foreground mb-1">
                    Employer contribution they chose
                  </p>
                  <div className="text-sm bg-gray-50 dark:bg-gray-800 p-3 rounded-lg space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Employee tier</span>
                      <span className="font-medium tabular-nums">
                        {format(employeeShare, strategy.employerFlatEmployeeTier)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Dependants</span>
                      <span className="font-medium tabular-nums">
                        {format(dependentShare, strategy.employerFlatDependents)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}
            {acceptance.comments && (
              <div className="pt-2">
                <p className="text-sm text-muted-foreground mb-1">Comments</p>
                <p className="text-sm bg-gray-50 dark:bg-gray-800 p-3 rounded-lg italic">
                  "{acceptance.comments}"
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Selection Summary
            </CardTitle>
            <CardDescription>
              Plans selected by the client
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Selected Carrier</p>
                <p className="font-medium">{selectedCarrier}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Plans Selected</p>
                <p className="font-medium">{selectedPlansJson?.length || 0} plan{(selectedPlansJson?.length || 0) !== 1 ? "s" : ""}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground italic pt-2">
              Actual premiums will be calculated based on employee enrollment selections.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Selected Plans
          </CardTitle>
          <CardDescription>
            Plans chosen by the client for enrollment
          </CardDescription>
        </CardHeader>
        <CardContent>
          {selectedPlansJson && selectedPlansJson.length > 0 ? (
            <div className="space-y-4">
              {selectedPlansJson.map((plan, idx) => {
                const Icon = getCoverageIcon(plan.coverageType);
                const colorClass = getCoverageColor(plan.coverageType);
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border"
                  >
                    <div className="flex items-center gap-4">
                      <div className={cn("p-3 rounded-lg", colorClass)}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="font-semibold text-lg">{plan.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {plan.carrier || plan.carrierName}
                          {plan.metalTier && (
                            <Badge variant="outline" className="ml-2 capitalize">
                              {plan.metalTier}
                            </Badge>
                          )}
                        </div>
                        <Badge variant="secondary" className="mt-1 capitalize text-[10px]">
                          {plan.coverageType}
                        </Badge>
                      </div>
                    </div>
                    {/* The split is the point of this page, so give it the same
                        labelled layout the client saw rather than a pipe-separated
                        line. Percentages make a wrong split obvious at a glance. */}
                    <div className="text-right shrink-0">
                      <div className="text-xl font-bold tabular-nums">
                        {formatCurrency(plan.premium)}
                        <span className="text-sm font-normal text-muted-foreground">/mo</span>
                      </div>
                      <div className="mt-1.5 space-y-0.5 text-sm">
                        <div className="flex items-baseline justify-end gap-2">
                          <span className="text-muted-foreground">Employer</span>
                          <span className="font-medium tabular-nums text-emerald-700 dark:text-emerald-500 w-24 text-right">
                            {formatCurrency(plan.employerPays)}
                          </span>
                          <span className="text-xs text-muted-foreground w-9 text-right">
                            {plan.premium ? `${Math.round((plan.employerPays / plan.premium) * 100)}%` : ""}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-end gap-2">
                          <span className="text-muted-foreground">Employee</span>
                          <span className="font-medium tabular-nums w-24 text-right">
                            {formatCurrency(plan.employeePays)}
                          </span>
                          <span className="text-xs text-muted-foreground w-9 text-right">
                            {plan.premium ? `${Math.round((plan.employeePays / plan.premium) * 100)}%` : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No specific plans were recorded with this acceptance.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {isPending ? (
        <Card className="border-2 border-dashed border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="py-8 text-center">
            <h3 className="text-lg font-semibold mb-2">Review the selections above</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-4">
              The client submitted these selections. Click <strong>Confirm &amp; Accept</strong> to
              finalize the quote as accepted.
            </p>
            <Button
              onClick={() => confirmMutation.mutate()}
              disabled={confirmMutation.isPending}
              className="gap-2"
            >
              <CheckCircle2 className="h-4 w-4" />
              {confirmMutation.isPending ? "Confirming..." : "Confirm & Accept"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2 border-dashed border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
              <ArrowRight className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            {/* Once plans exist, show the outcome rather than offering to create
                them again — running it twice would duplicate the client's plans. */}
            {conversion ? (
              <>
                <h3 className="text-lg font-semibold mb-2">Plans created</h3>
                <p className="text-muted-foreground max-w-md mx-auto mb-4">
                  {conversion.planIds.length} plan
                  {conversion.planIds.length === 1 ? "" : "s"} from this quote are now offered to
                  the client. Employees can't enrol until you open an enrollment window on their
                  Benefits tab.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Button asChild>
                    <Link href={`/clients/${conversion.clientId}?tab=benefits&subtab=open-enrollment`}>
                      <Calendar className="h-4 w-4 mr-2" />
                      Open enrollment
                    </Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link href={`/clients/${conversion.clientId}?tab=benefits&newPlans=${conversion.planIds.join(",")}`}>
                      <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600 dark:text-emerald-500" />
                      View them
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-muted-foreground"
                    onClick={() => setCreatePlansOpen(true)}
                  >
                    Create more
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-2">Set up the client's benefits</h3>
                <p className="text-muted-foreground max-w-md mx-auto mb-4">
                  Add the plans the client accepted to their benefits, with the contribution split
                  they chose. You'll see exactly what gets created before anything is saved.
                </p>
                <Button className="gap-2" onClick={() => setCreatePlansOpen(true)}>
                  <ArrowRight className="h-4 w-4" />
                  Create client plans
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <CreateClientPlansDialog
        quoteId={quoteId}
        open={createPlansOpen}
        onOpenChange={setCreatePlansOpen}
      />
    </div>
  );
}
